import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, Info, CheckCircle2, AlertOctagon, X } from "lucide-react";

const SEVERITY_META = {
  info:     { icon: Info,          bg: "bg-sky-50",     border: "border-sky-300",    text: "text-sky-900",     accent: "text-sky-600" },
  warning:  { icon: AlertTriangle, bg: "bg-amber-50",   border: "border-amber-300",  text: "text-amber-900",   accent: "text-amber-600" },
  critical: { icon: AlertOctagon,  bg: "bg-red-50",     border: "border-red-300",    text: "text-red-900",     accent: "text-red-600" },
  success:  { icon: CheckCircle2,  bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", accent: "text-emerald-600" },
};

// Session-scoped dismissals: cleared on new session (reopen browser tab).
// Requirement: pop-up reappears when the user reopens the system.
const DISMISS_KEY = "vyntric_dismissed_announcements";
const readDismissed = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"));
  } catch (_e) {
    return new Set();
  }
};
const persistDismissed = (set) => {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(set))); } catch (_e) { /* ignore */ }
};

export default function AnnouncementBanners() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/active");
      setItems(Array.isArray(data) ? data : []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    // Poll every 60s so newly-created announcements show up without needing refresh.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    persistDismissed(next);
  };

  const visible = items.filter((a) => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="announcement-banners">
      {visible.map((a) => {
        const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
        const Icon = meta.icon;
        return (
          <div
            key={a.id}
            data-testid={`announcement-${a.id}`}
            className={`border ${meta.border} ${meta.bg} ${meta.text} px-4 py-3 flex items-start gap-3 shadow-sm`}
          >
            <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${meta.accent}`} />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm">{a.title}</div>
              <div className="text-xs mt-0.5 leading-relaxed whitespace-pre-wrap">{a.message}</div>
            </div>
            {a.dismissible && (
              <button
                type="button"
                onClick={() => dismiss(a.id)}
                data-testid={`announcement-dismiss-${a.id}`}
                className={`shrink-0 p-1 hover:bg-black/5 rounded-sm ${meta.accent}`}
                title="Fechar (reaparece na próxima sessão)"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
